import React, { useState } from 'react';
import styled from 'styled-components';
import Drawer from '../components/drawer';
import { useMainLayout } from './routeMainLayout/context';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../redux/store';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { endSession } from '../screens/auth/login/actions';
import { tr } from '../translation';
import { ChatItemType } from '../interfaces/api/qna';
import {
  setMessages,
  selectChat,
  setLoadingChatMessages,
  resetChatState,
  setSelectedChatLayoutDirection,
} from '../screens/content/chat/reducer';
import { showGlobalModal } from '../connected-components/globalModal/actions';
import { decryptUrlParam, encryptUrlParam } from 'utils/security';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: ${({ theme }) => theme.homeBackground};
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  position: relative;
`;

interface ContentProps {
  $showDrawer?: boolean;
}

const Content = styled.div<ContentProps>`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin-left: ${(props) => (props.$showDrawer ? '280px' : '0')};
  transition: margin-left 0.3s ease;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { props } = useMainLayout();
  // eslint-disable-next-line
  const { showDrawer = true } = props;
  const { selectedChatId } = useSelector((state: RootState) => state.chat);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isNavigating, setIsNavigating] = useState(false);

  const { chatId, layout, tabId } = useParams<{ chatId?: string; layout?: string; tabId?: string }>();

  const handleCreateNewChat = () => {
    // Reset chat state
    dispatch(setMessages({ messages: [] }));
    dispatch(selectChat(null));
    dispatch(setLoadingChatMessages(false));
    navigate(`/${encryptUrlParam(currentTabId)}/chats`);
  };

  const handleChatItemPress = async (chat: ChatItemType) => {
    // Check if we're already on this chat thread to prevent unnecessary navigation
    if (selectedChatId && +selectedChatId === +chat.id) {
      // Already on this chat thread, don't navigate again
      return;
    }

    dispatch(setMessages({ messages: [] }));
    dispatch(selectChat(chat.id.toString()));
    dispatch(setSelectedChatLayoutDirection(chat.layout));
    // Always navigate to the chat with the layout preserved
    navigate(`/${tabId}/chats/${encryptUrlParam(chat.id?.toString())}/${encryptUrlParam(chat.layout)}`);
  };

  const handleBackToTabs = () => {
    // Set navigating state to true
    setIsNavigating(true);

    // Reset the entire chat state
    dispatch(resetChatState());

    // Navigate to tabs
    navigate(`/${tabId}`);

    // Reset navigating state after a short delay
    setTimeout(() => {
      setIsNavigating(false);
    }, 500);
  };

  const handleLogout = async () => {
    showGlobalModal({
      title: tr('afterLogin.logoutTitle'),
      message: tr('afterLogin.logoutMessage'),
      onConfirm: () => {
        endSession();
        dispatch(resetChatState());
      },
      type: 'question',
    });
  };

  // Only show drawer on chat-related routes
  const isChatRoute = location.pathname.includes('/chats');
  const shouldShowDrawer = showDrawer && isChatRoute;

  // Get the current tab_id for the key
  const currentTabId = decryptUrlParam(tabId);

  return (
    <Container>
      <MainContent>
        {shouldShowDrawer && (
          <Drawer
            key={`drawer-${currentTabId}`}
            isVisible={true}
            onCreateNewChatPressed={handleCreateNewChat}
            onChatItemPressed={handleChatItemPress}
            onLogoutPressed={handleLogout}
            selectedChatId={selectedChatId}
            isLoading={false}
            onBackPressed={handleBackToTabs}
          />
        )}
        <Content $showDrawer={shouldShowDrawer}>{children}</Content>
      </MainContent>
    </Container>
  );
};

export default MainLayout;
